import { useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useToasts, type ToastTone } from '@/stores/toast';

const TONE_STYLES: Record<ToastTone, string> = {
  ok: 'border-ok/50 bg-ok-soft text-ok',
  info: 'border-info/50 bg-info-soft text-info',
  warn: 'border-warn/50 bg-warn-soft text-warn',
  danger: 'border-danger/50 bg-danger-soft text-danger',
};

const TONE_ICONS: Record<ToastTone, typeof Info> = {
  ok: CheckCircle2,
  info: Info,
  warn: AlertTriangle,
  danger: XCircle,
};

export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);

  useEffect(() => {
    const timers = toasts
      .filter((toast) => toast.ttlMs > 0)
      .map((toast) => window.setTimeout(() => dismiss(toast.id), toast.ttlMs));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [toasts, dismiss]);

  if (toasts.length === 0) return null;

  return (
    <div
      /*
       * На телефон известието стои НАД долната лента: сложено на 16 px от
       * дъното, то ляга точно върху бутоните и „Затвори" не се улучва.
       */
      className="pointer-events-none fixed inset-x-3 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-[60] flex flex-col gap-2 lg:inset-x-auto lg:bottom-4 lg:right-4 lg:w-[min(26rem,calc(100vw-2rem))]"
      role="status"
      aria-live="polite"
    >
      {toasts.map((toast) => {
        const Icon = TONE_ICONS[toast.tone];
        return (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2 rounded-lg border px-3 py-2 shadow-lg backdrop-blur',
              TONE_STYLES[toast.tone],
            )}
          >
            <Icon className="mt-0.5 size-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink">{toast.title}</div>
              {toast.body ? (
                <div className="mt-0.5 break-words text-xs text-ink-dim">{toast.body}</div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="shrink-0 rounded p-0.5 text-ink-faint hover:text-ink"
              aria-label="Затвори известието"
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
